// ═══════════════════════════════════════════════════════════════════════════
import { safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safeStorage";
// attribution.ts — captura e preserva atribuição de marketing no cliente:
//   UTM (source/medium/campaign/content/term), fbclid, gclid e os cookies do
//   Meta Pixel (_fbp, _fbc). O primeiro toque da sessão fica em localStorage e
//   é anexado aos analytics_events (page_view, register, checkout, payment...).
// Não altera o checkout — apenas enriquece o rastreamento existente.
// ═══════════════════════════════════════════════════════════════════════════

const ATTR_KEY = "blink_attribution";

export interface Attribution {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_adset?: string | null;
  // Parâmetros dinâmicos das campanhas Meta Ads atuais (utm_id, IDs, posicionamento).
  utm_id?: string | null;
  meta_campaign_id?: string | null;
  meta_adset_id?: string | null;
  meta_ad_id?: string | null;
  utm_placement?: string | null;
  site_source?: string | null;
  fbclid?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  fbp?: string | null;
  fbc?: string | null;
  referrer_hostname?: string | null;
  landing_path?: string | null;
  captured_at: string;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function readUrlParams(): Record<string, string | null> {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const pick = (k: string) => {
    const v = p.get(k);
    return v ? v : null;
  };
  return {
    utm_source: pick("utm_source"),
    utm_medium: pick("utm_medium"),
    utm_campaign: pick("utm_campaign"),
    utm_content: pick("utm_content"),
    utm_term: pick("utm_term"),
    // utm_adset: parâmetro nativo do Meta Ads (URL parameters dinâmicos,
    // {{adset.name}}) — sem isso o "conjunto" nunca é capturado de verdade e
    // cai só no fallback de utm_term (que é semanticamente palavra-chave).
    utm_adset: pick("utm_adset"),
    utm_id: pick("utm_id"),
    meta_campaign_id: pick("meta_campaign_id"),
    meta_adset_id: pick("meta_adset_id"),
    meta_ad_id: pick("meta_ad_id"),
    utm_placement: pick("utm_placement"),
    site_source: pick("site_source"),
    fbclid: pick("fbclid"),
    gclid: pick("gclid"),
    // Google Ads em iOS/privacidade: substituem o gclid quando ele não vem.
    gbraid: pick("gbraid"),
    wbraid: pick("wbraid"),
  };
}

/** Houve UTM ou ID de clique NOVO nesta URL? (define a troca de last_touch) */
function hasNewSignal(p: Record<string, string | null>): boolean {
  return Boolean(
    p.utm_source || p.utm_medium || p.utm_campaign || p.utm_content || p.utm_term ||
    p.utm_id || p.utm_adset || p.utm_placement ||
    p.meta_campaign_id || p.meta_adset_id || p.meta_ad_id ||
    p.fbclid || p.gclid || p.gbraid || p.wbraid
  );
}

/** A URL ATUAL traz parâmetro de campanha? (decide se precisa recapturar) */
function urlTemSinalDeCampanha(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return hasNewSignal(readUrlParams());
  } catch {
    return false;
  }
}

/**
 * Registro fora da janela de 90 dias? Olha o `first_touch` (início da janela);
 * sem ele, cai no `captured_at` da raiz. Usado na LEITURA, não só na escrita.
 */
function registroVencido(a: StoredAttribution): boolean {
  return idadeEmDias(a.first_touch?.captured_at || a.captured_at) > ATTR_TTL_DIAS;
}

/**
 * `fbc` no formato que a Meta exige: `fb.1.<timestamp ms>.<fbclid>`.
 * Usado quando o cookie `_fbc` ainda não existe (o Pixel pode não ter rodado,
 * ou o clique acabou de acontecer) mas o `fbclid` veio na URL — sem isso a CAPI
 * perde a ligação com o clique do anúncio.
 */
export function buildFbc(fbclid?: string | null, whenMs?: number): string | null {
  if (!fbclid) return null;
  return `fb.1.${whenMs || Date.now()}.${fbclid}`;
}

/** Lê a atribuição já capturada (ou captura na hora se ainda não existir). */
export function getAttribution(): Attribution {
  if (typeof window === "undefined") {
    return { captured_at: new Date().toISOString() };
  }
  // BUG CORRIGIDO (13/10/2026, apontado pela agência): antes isto devolvia o
  // registro salvo SEMPRE que ele existisse, e só chamava captureAttribution()
  // quando não havia nada — então o last_touch nunca trocava (campanha A depois
  // B continuava marcando A). Se a URL atual traz parâmetro de campanha, a
  // captura tem de rodar ANTES da leitura, porque é ela que atualiza o
  // last_touch.
  if (urlTemSinalDeCampanha()) return captureAttribution();
  try {
    const raw = safeGetItem(ATTR_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredAttribution;
      // Expiração de 90 dias também na LEITURA (pedido da agência): sem isto,
      // um registro vencido continuava sendo devolvido indefinidamente.
      if (parsed && parsed.captured_at && !registroVencido(parsed)) return hydrate(parsed);
    }
  } catch {
    /* ignore */
  }
  return captureAttribution();
}

/**
 * Captura o primeiro toque da sessão (idempotente): só grava na primeira vez,
 * para que o UTM/fbclid do anúncio que trouxe o usuário seja preservado mesmo
 * que ele navegue internamente (SPA) depois.
 */
export function captureAttribution(): Attribution {
  const params = readUrlParams();
  const fbp = readCookie("_fbp");
  const fbc = readCookie("_fbc");
  const attr: Attribution = {
    utm_source: params.utm_source,
    utm_medium: params.utm_medium,
    utm_campaign: params.utm_campaign,
    utm_content: params.utm_content,
    utm_term: params.utm_term,
    utm_adset: params.utm_adset,
    utm_id: params.utm_id,
    meta_campaign_id: params.meta_campaign_id,
    meta_adset_id: params.meta_adset_id,
    meta_ad_id: params.meta_ad_id,
    utm_placement: params.utm_placement,
    site_source: params.site_source,
    fbclid: params.fbclid,
    gclid: params.gclid,
    gbraid: params.gbraid,
    wbraid: params.wbraid,
    fbp,
    fbc: fbc || buildFbc(params.fbclid),
    referrer_hostname:
      typeof document !== "undefined" && document.referrer
        ? (() => {
            try {
              return new URL(document.referrer).hostname;
            } catch {
              return null;
            }
          })()
        : null,
    landing_path:
      typeof window !== "undefined" ? window.location.pathname : null,
    captured_at: new Date().toISOString(),
  };

  // BUG CORRIGIDO (13/10/2026, apontado pela agência): isto aceitava `fbp`/`fbc`
  // sozinhos. O cookie `_fbp` existe em QUALQUER visita (o Pixel cria no primeiro
  // load, sem anúncio nenhum), então uma visita orgânica já gravava um registro
  // "vazio" — e esse registro, por existir, bloqueava todas as campanhas seguintes.
  // Agora só grava com parâmetro REAL de campanha; `fbp`/`fbc` seguem sendo
  // salvos junto quando há sinal, mas nunca criam registro sozinhos.
  const hasSignal = hasNewSignal(params);

  // ── first_touch × last_touch (13/10/2026, pedido da agência) ────────────
  // Antes isto gravava UMA vez e nunca mais atualizava: quem voltava por um
  // anúncio novo continuava sendo creditado à campanha antiga.
  //
  // FORMATO (não mudar sem avisar a agência): a chave continua
  // `blink_attribution` e os campos de LAST TOUCH continuam na RAIZ do objeto
  // — outra parte do tracking lê desse jeito. `first_touch` e `last_touch`
  // entram como campos extras, sem quebrar quem já lê a raiz.
  if (typeof window !== "undefined") {
    try {
      const rawAnterior = safeGetItem(ATTR_KEY);
      const anterior = rawAnterior ? (JSON.parse(rawAnterior) as StoredAttribution) : null;
      const vencido = anterior ? registroVencido(anterior) : false;

      if (!anterior || vencido) {
        // Primeira visita (ou janela de 90 dias expirada): só grava com sinal.
        if (hasSignal) salvar({ ...attr, first_touch: attr, last_touch: attr });
        // Registro VENCIDO sem sinal novo: apagar de vez. Sem isto ele continuava
        // no storage e a próxima leitura o devolvia — a expiração só valia na
        // escrita, nunca na prática (pego no teste de 13/10/2026: registro de 2020
        // ainda era devolvido como campanha atual).
        else if (vencido) remover();
      } else if (hasNewSignal(params)) {
        // Voltou por um anúncio NOVO → troca o last_touch, preserva o first.
        salvar({ ...attr, first_touch: anterior.first_touch || stripMeta(anterior), last_touch: attr });
      } else {
        // Navegação interna sem parâmetro novo: mantém o que já estava.
        return hydrate(anterior);
      }
    } catch {
      /* storage bloqueado — segue só em memória */
    }
  }
  return attr;
}

/** Validade da janela de atribuição (pedido da agência: 90 dias). */
const ATTR_TTL_DIAS = 90;

interface StoredAttribution extends Attribution {
  first_touch?: Attribution;
  last_touch?: Attribution;
}

function idadeEmDias(iso?: string): number {
  if (!iso) return Infinity;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return Infinity;
  return (Date.now() - t) / 86400000;
}

/** Remove os aninhados, deixando só os campos planos (um "toque" puro). */
function stripMeta(a: StoredAttribution): Attribution {
  const { first_touch: _f, last_touch: _l, ...plano } = a;
  void _f; void _l;
  return plano as Attribution;
}

function salvar(obj: StoredAttribution) {
  try { safeSetItem(ATTR_KEY, JSON.stringify(obj)); } catch { /* ignore */ }
}

/** Descarta o registro (usado quando a janela de 90 dias expirou). */
function remover() {
  try { safeRemoveItem(ATTR_KEY); } catch { /* ignore */ }
}

/** Garante que a raiz reflita o last_touch (compatibilidade com o formato antigo). */
function hydrate(a: StoredAttribution): Attribution {
  const ultimo = a.last_touch || stripMeta(a);
  return { ...ultimo, first_touch: a.first_touch, last_touch: ultimo } as Attribution;
}

/** Atribuição completa (com first_touch/last_touch) — usada no checkout. */
export function getFullAttribution(): StoredAttribution {
  if (typeof window === "undefined") return { captured_at: new Date().toISOString() };
  // Mesma correção de `getAttribution` (13/10/2026): URL com campanha atualiza o
  // last_touch antes de ler, e registro vencido (90 dias) não é mais devolvido.
  // Vale especialmente aqui, que é o que vai no metadata do Pagar.me.
  if (urlTemSinalDeCampanha()) return captureAttribution() as StoredAttribution;
  try {
    const raw = safeGetItem(ATTR_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredAttribution;
      if (parsed && parsed.captured_at && !registroVencido(parsed)) return hydrate(parsed) as StoredAttribution;
    }
  } catch { /* ignore */ }
  return captureAttribution() as StoredAttribution;
}

/** Mescla a atribuição capturada em qualquer objeto de parâmetros de evento. */
export function mergeAttribution(params: Record<string, any> = {}): Record<string, any> {
  const attr = getAttribution();
  const out: Record<string, any> = { ...params };
  const keys: (keyof Attribution)[] = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_adset",
    "utm_id",
    "meta_campaign_id",
    "meta_adset_id",
    "meta_ad_id",
    "utm_placement",
    "site_source",
    "fbclid",
    "gclid",
    "gbraid",
    "wbraid",
    "fbp",
    "fbc",
    "referrer_hostname",
    "landing_path",
  ];
  for (const k of keys) {
    const v = attr[k];
    if (v && !out[k]) out[k] = v;
  }
  return out;
}
